import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Download, Trash2, Mail, Phone, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import {
  fetchConsultations,
  updateConsultationStatus,
  deleteConsultation,
  fetchPitchLeads,
  fetchInvestorInquiries,
  fetchVillaWaitlist,
  consultationsCsvUrl,
  formatApiError,
} from "@/lib/api";

const STATUS_OPTS = ["new", "contacted", "closed"];
const STATUS_COLORS = {
  new: "border-[#D4AF37] text-[#D4AF37]",
  contacted: "border-blue-400 text-blue-400",
  closed: "border-[#8FA3B8] text-[#8FA3B8]",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("consultations");
  const [consultations, setConsultations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, l, iq, wl] = await Promise.all([
        fetchConsultations(),
        fetchPitchLeads(),
        fetchInvestorInquiries(),
        fetchVillaWaitlist(),
      ]);
      setConsultations(c);
      setLeads(l);
      setInquiries(iq);
      setWaitlist(wl);
    } catch (err) {
      setError(formatApiError(err, "Failed to load data."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredConsults = useMemo(
    () => (statusFilter === "all" ? consultations : consultations.filter((c) => c.status === statusFilter)),
    [consultations, statusFilter]
  );

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await updateConsultationStatus(id, status);
      setConsultations((prev) => prev.map((c) => (c.id === id ? { ...c, ...updated } : c)));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await deleteConsultation(id);
      setConsultations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const counts = useMemo(() => {
    const c = { new: 0, contacted: 0, closed: 0 };
    consultations.forEach((x) => {
      c[x.status] = (c[x.status] || 0) + 1;
    });
    return c;
  }, [consultations]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A1628] text-[#F3EFE4]">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0A1628]/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="admin-brand">
            <img src="/brand/unexx-mark.png" alt="U-NEXX" className="h-10 w-auto object-contain" />
            <div className="leading-tight border-l border-white/15 pl-3">
              <div className="font-serif-display text-base">U-NEXX</div>
              <div className="text-[0.55rem] tracking-[0.32em] text-[#8FA3B8] uppercase whitespace-nowrap">Admin Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-xs text-[#8FA3B8]">{user?.email}</div>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="text-[#8FA3B8] hover:text-[#D4AF37] transition p-2"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn-secondary !py-2 !px-4 !text-[0.7rem]">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="overline mb-3">Lead Inbox</div>
            <h1 className="font-serif-display text-4xl md:text-5xl">All submissions in one place.</h1>
          </div>
          <a
            href={consultationsCsvUrl}
            data-testid="admin-export-csv"
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
          >
            Export CSV <Download size={16} />
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10 mb-10">
          <StatCell label="Consultations" value={consultations.length} />
          <StatCell label="Investor inquiries" value={inquiries.length} accent />
          <StatCell label="Villa waitlist" value={waitlist.length} accent />
          <StatCell label="Pitch-deck leads" value={leads.length} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10 mb-6 overflow-x-auto">
          <TabBtn id="consultations" active={tab} onClick={setTab} count={consultations.length} />
          <TabBtn id="investor-inquiries" active={tab} onClick={setTab} count={inquiries.length} />
          <TabBtn id="villa-waitlist" active={tab} onClick={setTab} count={waitlist.length} />
          <TabBtn id="pitch-deck" active={tab} onClick={setTab} count={leads.length} />
        </div>

        {error && (
          <div data-testid="admin-error" className="mb-6 text-sm text-red-400 border border-red-500/30 px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-[#8FA3B8] tracking-[0.3em] text-xs uppercase">Loading…</div>
        ) : tab === "consultations" ? (
          <>
            {/* Status filter */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto">
              {["all", ...STATUS_OPTS].map((s) => (
                <button
                  key={s}
                  data-testid={`admin-filter-${s}`}
                  onClick={() => setStatusFilter(s)}
                  className={`text-[0.7rem] tracking-[0.24em] uppercase px-3 py-2 transition border ${
                    statusFilter === s ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#8FA3B8] hover:text-white"
                  }`}
                >
                  {s} {s !== "all" && `(${counts[s] || 0})`}
                </button>
              ))}
            </div>

            {filteredConsults.length === 0 ? (
              <div className="py-16 text-center text-[#8FA3B8] text-sm">No submissions match this filter.</div>
            ) : (
              <div className="border border-white/10 divide-y divide-white/10">
                {filteredConsults.map((c) => (
                  <ConsultationRow
                    key={c.id}
                    item={c}
                    onStatus={handleStatusChange}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        ) : tab === "pitch-deck" ? (
          <PitchLeadsTable leads={leads} />
        ) : tab === "villa-waitlist" ? (
          <VillaWaitlistTable entries={waitlist} />
        ) : (
          <InvestorInquiriesTable inquiries={inquiries} />
        )}
      </main>
    </div>
  );
}

function StatCell({ label, value, accent }) {
  return (
    <div className="border-r border-b border-white/10 p-6 md:p-8">
      <div className="text-[0.62rem] tracking-[0.28em] uppercase text-[#8FA3B8] mb-3">{label}</div>
      <div className={`font-serif-display text-4xl md:text-5xl ${accent ? "text-[#D4AF37]" : "text-[#F3EFE4]"}`}>
        {value}
      </div>
    </div>
  );
}

function TabBtn({ id, active, onClick, count }) {
  const labels = {
    "consultations": "Consultations",
    "investor-inquiries": "Investor Inquiries",
    "villa-waitlist": "Villa Waitlist",
    "pitch-deck": "Pitch-Deck Leads",
  };
  const label = labels[id] || id;
  return (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => onClick(id)}
      className={`px-5 py-3 text-xs tracking-[0.24em] uppercase whitespace-nowrap transition border-b-2 -mb-px ${
        active === id ? "border-[#D4AF37] text-[#F3EFE4]" : "border-transparent text-[#8FA3B8] hover:text-white"
      }`}
    >
      {label} <span className="text-[#D4AF37] ml-1">({count})</span>
    </button>
  );
}

function ConsultationRow({ item, onStatus, onDelete }) {
  const created = new Date(item.created_at);
  return (
    <div data-testid={`admin-row-${item.id}`} className="p-5 md:p-6 grid md:grid-cols-[1.4fr_1.2fr_1fr_140px_80px] gap-4 items-start hover:bg-white/[0.02] transition">
      <div>
        <div className="font-serif-display text-xl text-[#F3EFE4]">{item.name}</div>
        <div className="text-xs text-[#8FA3B8] mt-1">
          {created.toLocaleDateString()} · {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <div className="space-y-1 text-sm">
        <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-[#F3EFE4] hover:text-[#D4AF37] transition truncate">
          <Mail size={13} className="shrink-0" /> {item.email}
        </a>
        {item.phone && (
          <a href={`tel:${item.phone.replace(/\D/g, "")}`} className="flex items-center gap-2 text-[#8FA3B8] hover:text-[#D4AF37] transition">
            <Phone size={13} className="shrink-0" /> {item.phone}
          </a>
        )}
      </div>
      <div>
        <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#8FA3B8] mb-1">{item.investment_type || "—"}</div>
        {item.budget && <div className="text-xs text-[#D4AF37]">{item.budget}</div>}
        <p className="text-xs text-[#8FA3B8] leading-relaxed mt-2 line-clamp-3 max-w-md">{item.message}</p>
      </div>
      <div>
        <span className={`inline-block text-[0.6rem] tracking-[0.22em] uppercase border px-2 py-1 mb-2 ${STATUS_COLORS[item.status] || STATUS_COLORS.new}`}>
          {item.status}
        </span>
        <select
          data-testid={`admin-status-${item.id}`}
          className="field-input !py-1 !text-xs !border-white/10"
          value={item.status}
          onChange={(e) => onStatus(item.id, e.target.value)}
        >
          {STATUS_OPTS.map((s) => (
            <option key={s} value={s} className="bg-[#12253D]">{s}</option>
          ))}
        </select>
      </div>
      <div className="flex md:justify-end">
        <button
          data-testid={`admin-delete-${item.id}`}
          onClick={() => onDelete(item.id)}
          className="text-[#8FA3B8] hover:text-red-400 transition p-2"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function PitchLeadsTable({ leads }) {
  if (leads.length === 0) {
    return <div className="py-16 text-center text-[#8FA3B8] text-sm">No pitch-deck downloads yet.</div>;
  }
  return (
    <div className="border border-white/10 divide-y divide-white/10">
      {leads.map((l) => {
        const created = new Date(l.created_at);
        return (
          <div key={l.id} data-testid={`admin-lead-${l.id}`} className="p-5 md:p-6 grid md:grid-cols-[1.2fr_1.2fr_1fr_1fr] gap-4 items-start hover:bg-white/[0.02] transition">
            <div>
              <div className="font-serif-display text-xl text-[#F3EFE4]">{l.name}</div>
              <div className="text-xs text-[#8FA3B8] mt-1">
                {created.toLocaleDateString()} · {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <a href={`mailto:${l.email}`} className="text-sm text-[#F3EFE4] hover:text-[#D4AF37] transition flex items-center gap-2">
              <Mail size={13} /> {l.email}
            </a>
            <div className="text-sm">
              <div className="text-[#F3EFE4]">{l.company || "—"}</div>
              <div className="text-xs text-[#8FA3B8]">{l.role || ""}</div>
            </div>
            <div className="text-xs tracking-[0.22em] uppercase text-[#D4AF37]">{l.investor_type || "—"}</div>
          </div>
        );
      })}
    </div>
  );
}

function InvestorInquiriesTable({ inquiries }) {
  if (inquiries.length === 0) {
    return <div className="py-16 text-center text-[#8FA3B8] text-sm">No investor inquiries yet.</div>;
  }
  return (
    <div className="border border-white/10 divide-y divide-white/10">
      {inquiries.map((iq) => {
        const created = new Date(iq.created_at);
        return (
          <div key={iq.id} data-testid={`admin-inquiry-${iq.id}`} className="p-5 md:p-6 grid md:grid-cols-[1.2fr_1.1fr_1fr_1.2fr] gap-4 items-start hover:bg-white/[0.02] transition">
            <div>
              <div className="font-serif-display text-xl text-[#F3EFE4]">{iq.name}</div>
              <div className="text-xs text-[#8FA3B8] mt-1">
                {created.toLocaleDateString()} · {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              {iq.country && <div className="text-[0.65rem] text-[#8FA3B8] mt-2 tracking-[0.2em] uppercase">{iq.country}</div>}
            </div>
            <div className="space-y-1 text-sm">
              <a href={`mailto:${iq.email}`} className="flex items-center gap-2 text-[#F3EFE4] hover:text-[#D4AF37] transition truncate">
                <Mail size={13} className="shrink-0" /> {iq.email}
              </a>
              {iq.phone && (
                <a href={`tel:${iq.phone.replace(/\D/g, "")}`} className="flex items-center gap-2 text-[#8FA3B8] hover:text-[#D4AF37] transition">
                  <Phone size={13} className="shrink-0" /> {iq.phone}
                </a>
              )}
            </div>
            <div className="text-xs">
              <div className="text-[#D4AF37] tracking-[0.22em] uppercase mb-1">{iq.investor_type || "—"}</div>
              <div className="text-[#F3EFE4]">{iq.ticket_size || "—"}</div>
              <div className="text-[0.65rem] text-[#8FA3B8] mt-1">{iq.package_preference || ""}</div>
            </div>
            <div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-[#8FA3B8] mb-1">{iq.project_interest || "General"}</div>
              {iq.message && <p className="text-xs text-[#8FA3B8] leading-relaxed line-clamp-3 max-w-md">{iq.message}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VillaWaitlistTable({ entries }) {
  if (entries.length === 0) {
    return <div className="py-16 text-center text-[#8FA3B8] text-sm">No villa waitlist signups yet.</div>;
  }
  return (
    <div className="border border-white/10 divide-y divide-white/10">
      {entries.map((w) => {
        const created = new Date(w.created_at);
        return (
          <div key={w.id} data-testid={`admin-waitlist-${w.id}`} className="p-5 md:p-6 grid md:grid-cols-[80px_1.2fr_1.3fr_1fr] gap-4 items-center hover:bg-white/[0.02] transition">
            <div className="font-serif-display text-3xl text-[#D4AF37] leading-none">#{w.position}</div>
            <div>
              <div className="font-serif-display text-xl text-[#F3EFE4]">{w.name || "—"}</div>
              <div className="text-xs text-[#8FA3B8] mt-1">
                {created.toLocaleDateString()} · {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <a href={`mailto:${w.email}`} className="flex items-center gap-2 text-sm text-[#F3EFE4] hover:text-[#D4AF37] transition truncate">
              <Mail size={13} className="shrink-0" /> {w.email}
            </a>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#D4AF37]">{w.preferred_villa || "Any Villa"}</div>
          </div>
        );
      })}
    </div>
  );
}
